// Externe Bibliotheken
import { useState, useEffect } from 'react';
import axios from 'axios';

// Interne Module
import * as storage from '@/_helper/storage';
import tracking from '@/_helper/tracking';
import { buildPostRequest } from '@/_helper/apiHelper';

// Komponenten
import LoadingOffersBar from '@/comp/loadingOffersBar';
import { BackButton } from '@/comp/backButton/backButton';
import Button from '@/comp/button/button';

import OfferList from './_comp/offerList/OfferList';
import OfferCards from './_comp/offerCards/OfferCards';
import type { CruiseOffers, TransferOffer } from '@/model/upsell/transfer';

interface TransferData {
    processToken?: string;
    handlingOffers?: Record<string, unknown>;
    data?: CruiseOffers;
    [key: string]: unknown;
}

const ERROR_WORDING = {
    transferOffer: 'Bitte wählen Sie eine Option aus.',
};

const Form = () => {
    const [data, setData] = useState<CruiseOffers>({});
    const [isLoading, setIsLoading] = useState(false);
    const [hasResult, setHasResult] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [transferOffer, setTransferOffer] = useState<string | null>(null);
    const [formValid, setFormValid] = useState(false);
    const [formVacancy, setFormVacancy] = useState(false);
    const [formErrors, setFormErrors] = useState({ transferOffer: '' });

    useEffect(() => {
        const offers = storage.get('transferOffersCruise');
        handleSearchTransfer(offers);
    }, []);

    function handleSearchTransfer(offers: TransferData | null) {
        if (offers?.data) {
            setData(offers.data);
            setIsLoading(false);
            setHasResult(true);
            return;
        }

        setIsLoading(true);

        const sendData = {
            action: 'searchTransfer',
            requestType: 'bookingData',
            bookingId: storage.get('bookingNumber') || '',
            firstPaxName: storage.get('firstPaxName') || '',
            travelStart: storage.get('travelStart') || '',
            requestSource: 'AurumTours-Customerarea',
        };

        axios(buildPostRequest(window.$serviceUrl, '/bookingData', JSON.stringify(sendData)))
            .then((response) => {
                const transfers = response.data.data.data.ibeResponse.transfers;

                setData(transfers);
                setHasResult(true);

                storage.set('transferOffersCruise', { data: transfers });
            })
            .catch((err) => {
                setError(err);
                setIsLoading(false);
            });
    }

    function handleOfferSelection(offerKey: string) {
        tracking('interaction', 'transfer-cruise');
        setTransferOffer(offerKey);
        setFormValid(true);
        setFormErrors({ transferOffer: '' });
    }

    function validateForm(): boolean {
        if (transferOffer === null) {
            setFormValid(false);
            setFormErrors({ transferOffer: ERROR_WORDING.transferOffer });
            return false;
        }
        setFormValid(true);
        return true;
    }

    function handleVacancy(event: React.SyntheticEvent) {
        event.preventDefault();

        if (!validateForm()) return;

        setFormVacancy(true);

        const sendVacancyData = {
            action: 'vacancyTransfer',
            transferOfferKey: transferOffer,
            requestType: 'bookingData',
            bookingId: storage.get('bookingNumber') || '',
            firstPaxName: storage.get('firstPaxName') || '',
            travelStart: storage.get('travelStart') || '',
            requestSource: 'AurumTours-Customerarea',
        };

        axios(buildPostRequest(window.$serviceUrl, '/bookingData', JSON.stringify(sendVacancyData)))
            .then((response) => {
                const { requestToken, processToken, transfers } = response.data.data.data.ibeResponse;

                const vacancyData =
                    transfers
                        .flatMap((service: { offers: Record<string, TransferOffer> }) => Object.values(service.offers))
                        .find((offer: TransferOffer) => offer.offerKey === transferOffer) ?? null;

                const bookStates = {
                    travelType: 'transferCruise',
                    vacancyToken: requestToken,
                    processToken,
                    offerId: transferOffer,
                    vacancyData,
                    ibeInstance: 'ibe_3',
                };

                sessionStorage.setItem('bookStates', JSON.stringify(bookStates));

                window.location.href = `#/buchung/${window.$content.extras['transfer'].link}`;
            })
            .catch((err) => {
                setError(err);
                setFormVacancy(false);
            });
    }

    if (error || data === undefined) {
        return (
            <div>
                <div className="error">Leider sind keine Transferangebote zu Ihren Buchungsdaten verfügbar.</div>
                <div className="user-action">
                    <BackButton class="col" target="blank" />
                </div>
            </div>
        );
    }

    if (isLoading) {
        if (hasResult) {
            setTimeout(() => setIsLoading(false), 2000);
        }
        return <LoadingOffersBar hasResult={hasResult} />;
    }

    return (
        <form
            className={formVacancy ? 'validate' : ''}
            autoComplete="off"
            onSubmit={handleVacancy}
            method="post"
            noValidate>
            <p className="txtC">Wählen Sie Ihre bevorzugte Transfermethode, um Ihre Buchung abzuschließen.</p>

            {data &&
                Object.keys(data).length > 0 &&
                Object.entries(data).map(([key]) => {
                    const cruiseOffers: CruiseOffers = { [key]: data[key] };
                    return (
                        <div key={key} className="offer-container">
                            <OfferList
                                cruiseOffers={cruiseOffers}
                                onSelection={handleOfferSelection}
                                selectedOffer={transferOffer}
                            />
                            <OfferCards
                                cruiseOffers={cruiseOffers}
                                selectedOffer={transferOffer}
                                onSelection={handleOfferSelection}
                            />
                        </div>
                    );
                })}

            {formErrors && (
                <div className="errors">
                    {Object.entries(formErrors).map(([key, value]) => (
                        <div className="error txtC" key={key}>
                            {value}
                        </div>
                    ))}
                </div>
            )}

            <div className="user-action">
                <BackButton class="col" target="blank" />

                <Button
                    handleClick={() => validateForm()}
                    valid={formValid}
                    validated={formVacancy}
                    type="submit"
                    label={formVacancy ? 'Daten werden übermittelt ...' : `Weiter zur Übersicht`}
                    icon="arrowForward"
                />
            </div>
        </form>
    );
};

export default Form;
